/**
 * Shippings form hook: create/edit/delete shippings.
 * Flow: pick supplier (searchable) -> load product_suppliers for that supplier -> pick product_supplier -> (edit: pick shipping) -> edit fields -> submit.
 */
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { getSuppliers } from '../../../services/supplierService';
import { getProductSuppliers } from '../../../services/productSuppliersService';
import {
  getShippings,
  createShipping,
  updateShipping,
  deleteShipping
} from '../../../services/shippingsService';
import { useClickOutside, createHandleChange, clearFormAlerts } from '../../common';
import { SUPPLIERS_QUERY_KEY, LIST_STALE_TIME_MS } from '../../common/utils/queryKeys';
import { emptyFormData, shippingToFormData } from './shippingFormUtils';
import { useAppStore } from '../../../store/appStore';

export function useShippingsForm() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const shippingsForm = useAppStore((s) => s.shippingsForm);
  const setShippingsForm = useAppStore((s) => s.setShippingsForm);
  const setInsertBasicData = useAppStore((s) => s.setInsertBasicData);
  const {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedSupplier,
    supplierSearchQuery,
    showSupplierDropdown,
    supplierFieldTouched,
    selectedProductSupplier,
    productSupplierSearchQuery,
    showProductSupplierDropdown,
    productSupplierFieldTouched,
    selectedShipping,
    showShippingDropdown,
    shippingSearchQuery,
    shippingFieldTouched
  } = shippingsForm;

  const setMode = React.useCallback((v) => setShippingsForm((prev) => ({ ...prev, mode: v })), [setShippingsForm]);
  const setFormData = React.useCallback(
    (v) =>
      setShippingsForm((prev) => ({
        ...prev,
        formData: typeof v === 'function' ? v(prev.formData) : v
      })),
    [setShippingsForm]
  );
  const setFormError = React.useCallback((v) => setShippingsForm((prev) => ({ ...prev, formError: v })), [setShippingsForm]);
  const setFormSuccess = React.useCallback((v) => setShippingsForm((prev) => ({ ...prev, formSuccess: v })), [setShippingsForm]);
  const setSubmitting = React.useCallback((v) => setShippingsForm((prev) => ({ ...prev, submitting: v })), [setShippingsForm]);
  const setDeleting = React.useCallback((v) => setShippingsForm((prev) => ({ ...prev, deleting: v })), [setShippingsForm]);
  const setSelectedSupplier = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, selectedSupplier: v })),
    [setShippingsForm]
  );
  const setSupplierSearchQuery = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, supplierSearchQuery: v })),
    [setShippingsForm]
  );
  const setShowSupplierDropdown = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, showSupplierDropdown: v })),
    [setShippingsForm]
  );
  const setSupplierFieldTouched = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, supplierFieldTouched: v })),
    [setShippingsForm]
  );
  const setSelectedProductSupplier = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, selectedProductSupplier: v })),
    [setShippingsForm]
  );
  const setProductSupplierSearchQuery = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, productSupplierSearchQuery: v })),
    [setShippingsForm]
  );
  const setShowProductSupplierDropdown = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, showProductSupplierDropdown: v })),
    [setShippingsForm]
  );
  const setProductSupplierFieldTouched = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, productSupplierFieldTouched: v })),
    [setShippingsForm]
  );
  const setSelectedShipping = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, selectedShipping: v })),
    [setShippingsForm]
  );
  const setShowShippingDropdown = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, showShippingDropdown: v })),
    [setShippingsForm]
  );
  const setShippingSearchQuery = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, shippingSearchQuery: v })),
    [setShippingsForm]
  );
  const setShippingFieldTouched = React.useCallback(
    (v) => setShippingsForm((prev) => ({ ...prev, shippingFieldTouched: v })),
    [setShippingsForm]
  );

  const supplierDropdownRef = React.useRef(null);
  const productSupplierDropdownRef = React.useRef(null);
  const shippingDropdownRef = React.useRef(null);

  const { data: suppliersResponse, isLoading: suppliersLoading } = useQuery({
    queryKey: SUPPLIERS_QUERY_KEY,
    queryFn: () => getSuppliers(token),
    enabled: !!token && supplierFieldTouched,
    staleTime: LIST_STALE_TIME_MS
  });

  const { data: productSuppliersResponse, isLoading: productSuppliersLoading } = useQuery({
    queryKey: ['product-suppliers', selectedSupplier?.id],
    queryFn: () => getProductSuppliers(token, { supplier_id: selectedSupplier.id }),
    enabled: !!token && !!selectedSupplier?.id
  });

  const { data: shippingsResponse, isLoading: shippingsLoading } = useQuery({
    queryKey: ['shippings', selectedProductSupplier?.id],
    queryFn: () => getShippings(token, { product_supplier_id: selectedProductSupplier.id }),
    enabled: !!token && !!selectedProductSupplier?.id && mode === 'edit'
  });

  const sectionLoading = suppliersLoading || productSuppliersLoading || shippingsLoading;
  React.useEffect(() => {
    setInsertBasicData((prev) => ({
      ...prev,
      sectionLoading: { ...prev.sectionLoading, shippings: sectionLoading }
    }));
  }, [sectionLoading, setInsertBasicData]);

  const suppliers = suppliersResponse?.suppliers ?? [];
  const productSuppliers = productSuppliersResponse?.product_suppliers ?? [];
  const shippings = shippingsResponse?.shippings ?? [];

  const filteredSuppliers = React.useMemo(() => {
    if (!supplierSearchQuery.trim()) return suppliers;
    const q = supplierSearchQuery.trim().toLowerCase();
    return suppliers.filter((s) => (s.name || '').toLowerCase().includes(q));
  }, [suppliers, supplierSearchQuery]);

  const filteredProductSuppliers = React.useMemo(() => {
    if (!productSupplierSearchQuery.trim()) return productSuppliers;
    const q = productSupplierSearchQuery.trim().toLowerCase();
    return productSuppliers.filter(
      (ps) =>
        (ps.product_name || '').toLowerCase().includes(q) ||
        String(ps.id).toLowerCase().includes(q) ||
        String(ps.product_id || '').toLowerCase().includes(q)
    );
  }, [productSuppliers, productSupplierSearchQuery]);

  const filteredShippings = React.useMemo(() => {
    if (!shippingSearchQuery.trim()) return shippings;
    const q = shippingSearchQuery.trim().toLowerCase();
    return shippings.filter(
      (s) =>
        (s.carrier || '').toLowerCase().includes(q) ||
        (s.ships_to_country || '').toLowerCase().includes(q) ||
        String(s.id).includes(q)
    );
  }, [shippings, shippingSearchQuery]);

  useClickOutside(
    [supplierDropdownRef, productSupplierDropdownRef, shippingDropdownRef],
    () => {
      setShowSupplierDropdown(false);
      setShowProductSupplierDropdown(false);
      setShowShippingDropdown(false);
    }
  );

  const handleChange = React.useMemo(
    () => createHandleChange(setFormData, setFormError, setFormSuccess),
    [setFormData, setFormError, setFormSuccess]
  );

  const handleModeChange = (newMode) => {
    setMode(newMode);
    clearFormAlerts(setFormError, setFormSuccess);
    setFormData(emptyFormData());
    setSelectedSupplier(null);
    setSupplierSearchQuery('');
    setShowSupplierDropdown(false);
    setSupplierFieldTouched(false);
    setSelectedProductSupplier(null);
    setProductSupplierSearchQuery('');
    setShowProductSupplierDropdown(false);
    setProductSupplierFieldTouched(false);
    setSelectedShipping(null);
    setShowShippingDropdown(false);
    setShippingSearchQuery('');
    setShippingFieldTouched(false);
  };

  const handleSupplierSearchChange = (e) => {
    setSupplierSearchQuery(e.target.value);
    setSelectedSupplier(null);
    setSelectedProductSupplier(null);
    setProductSupplierSearchQuery('');
    setSelectedShipping(null);
    setFormData(emptyFormData());
    setShowSupplierDropdown(true);
  };

  const handleSelectSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearchQuery(supplier?.name || '');
    setShowSupplierDropdown(false);
    setSelectedProductSupplier(null);
    setProductSupplierSearchQuery('');
    setSelectedShipping(null);
    setFormData(emptyFormData());
    clearFormAlerts(setFormError, setFormSuccess);
  };

  const handleProductSupplierSearchChange = (e) => {
    setProductSupplierSearchQuery(e.target.value);
    setSelectedProductSupplier(null);
    setSelectedShipping(null);
    setFormData((prev) => ({ ...prev, product_supplier_id: null }));
    setShowProductSupplierDropdown(true);
  };

  /** @param {{ id: number, product_id?: number, product_name?: string }} ps */
  const handleSelectProductSupplier = (ps) => {
    setSelectedProductSupplier(ps);
    setProductSupplierSearchQuery(ps ? (ps.product_name || '').trim() || '(Unnamed product)' : '');
    setShowProductSupplierDropdown(false);
    setSelectedShipping(null);
    if (mode === 'create') {
      setFormData((prev) => ({ ...prev, product_supplier_id: ps?.id ?? null }));
    } else {
      setFormData(emptyFormData());
    }
    clearFormAlerts(setFormError, setFormSuccess);
  };

  const handleShippingSearchChange = (e) => {
    setShippingSearchQuery(e.target.value);
    setSelectedShipping(null);
    setShowShippingDropdown(true);
  };

  const handleSelectShipping = (shipping) => {
    setSelectedShipping(shipping);
    setShippingSearchQuery(shipping ? (shipping.carrier || `Shipping #${shipping.id}`) : '');
    setShowShippingDropdown(false);
    if (shipping) setFormData(shippingToFormData(shipping));
    clearFormAlerts(setFormError, setFormSuccess);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearFormAlerts(setFormError, setFormSuccess);
    setSubmitting(true);
    try {
      const productSupplierId = mode === 'create' ? formData.product_supplier_id : selectedShipping?.product_supplier_id ?? formData.product_supplier_id;
      if (productSupplierId == null) {
        setFormError('Please select a supplier and a product.');
        setSubmitting(false);
        return;
      }
      const payload = {
        product_supplier_id: productSupplierId,
        ships_to_country: formData.ships_to_country?.trim() || null,
        ships_to_region: formData.ships_to_region?.trim() || null,
        lead_time_days: formData.lead_time_days === '' ? null : Number(formData.lead_time_days),
        carrier: formData.carrier?.trim() || null,
        freight_type: formData.freight_type?.trim() || null,
        freight_price: formData.freight_price === '' ? null : Number(formData.freight_price),
        unit_of_price: formData.unit_of_price?.trim() || null
      };

      if (mode === 'create') {
        const result = await createShipping(token, payload);
        if (!result.success) {
          setFormError(result.error || 'Failed to create shipping');
          setSubmitting(false);
          return;
        }
        setFormSuccess('Shipping created successfully!');
        setFormData(emptyFormData());
        setSelectedProductSupplier(null);
        setProductSupplierSearchQuery('');
        queryClient.invalidateQueries({ queryKey: ['shippings'] });
      } else {
        if (!selectedShipping?.id) {
          setFormError('Please select a shipping to edit.');
          setSubmitting(false);
          return;
        }
        const result = await updateShipping(token, selectedShipping.id, payload);
        if (!result.success) {
          setFormError(result.error || 'Failed to update shipping');
          setSubmitting(false);
          return;
        }
        setFormSuccess('Changes saved successfully!');
        setFormData(emptyFormData());
        setSelectedShipping(null);
        setShippingSearchQuery('');
        setSelectedProductSupplier(null);
        setProductSupplierSearchQuery('');
        queryClient.invalidateQueries({ queryKey: ['shippings', selectedProductSupplier?.id] });
        queryClient.invalidateQueries({ queryKey: ['shippings'] });
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      setFormError(msg || (mode === 'create' ? 'Failed to create shipping' : 'Failed to update shipping'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedShipping?.id) return;
    clearFormAlerts(setFormError, setFormSuccess);
    setDeleting(true);
    try {
      const result = await deleteShipping(token, selectedShipping.id);
      if (result.success) {
        setFormSuccess('Shipping deleted.');
        setFormData(emptyFormData());
        setSelectedShipping(null);
        setShippingSearchQuery('');
        queryClient.invalidateQueries({ queryKey: ['shippings', selectedProductSupplier?.id] });
        queryClient.invalidateQueries({ queryKey: ['shippings'] });
      } else {
        setFormError(result.error || 'Failed to delete shipping');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      setFormError(msg || 'Failed to delete shipping');
    } finally {
      setDeleting(false);
    }
  };

  const isEditMode = mode === 'edit';

  /** Label for product_supplier option in dropdown (product_name from backend join) */
  const getProductSupplierLabel = (ps) =>
    ps ? ((ps.product_name || '').trim() || '(Unnamed product)') : '';

  /** Label for shipping option in dropdown */
  const getShippingLabel = (s) => (s ? (s.carrier || `Shipping #${s.id}`) : '');

  return {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedSupplier,
    supplierSearchQuery,
    showSupplierDropdown,
    supplierFieldTouched,
    suppliersLoading,
    filteredSuppliers,
    supplierDropdownRef,
    selectedProductSupplier,
    productSupplierSearchQuery,
    showProductSupplierDropdown,
    productSupplierFieldTouched,
    productSuppliersLoading,
    filteredProductSuppliers,
    productSupplierDropdownRef,
    selectedShipping,
    shippingSearchQuery,
    showShippingDropdown,
    shippingFieldTouched,
    shippingsLoading,
    filteredShippings,
    shippingDropdownRef,
    handleModeChange,
    handleChange,
    handleSupplierSearchChange,
    handleSelectSupplier,
    handleProductSupplierSearchChange,
    handleSelectProductSupplier,
    handleShippingSearchChange,
    handleSelectShipping,
    handleSubmit,
    handleDelete,
    isEditMode,
    setSupplierFieldTouched,
    setShowSupplierDropdown,
    setProductSupplierFieldTouched,
    setShowProductSupplierDropdown,
    setShippingFieldTouched,
    setShowShippingDropdown,
    getProductSupplierLabel,
    getShippingLabel
  };
}
